import React, { useEffect, useLayoutEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { LanguageProvider } from "./contexts/LanguageContext";
import { AuthProvider } from "./contexts/AuthContext";
import Navbar from "./components/Navbar";
import MaintenancePage from "./pages/MaintenancePage";

// MAINTENANCE MODE - Set to true to show "Lavori in corso" page
const MAINTENANCE_MODE = true;

// ScrollToTop component - scrolls to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  // Use useLayoutEffect for immediate scroll before paint
  useLayoutEffect(() => {
    // Scroll to top immediately
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    
    // Also set scroll on document element and body for full coverage
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);
  
  return null;
};
import HomePage from "./pages/HomePage";
import TestsPage from "./pages/TestsPage";
import TestPage from "./pages/TestPage";
import ResultsPage from "./pages/ResultsPage";
import AuthPage from "./pages/AuthPage";
import DashboardPage from "./pages/DashboardPage";
import PricingPage from "./pages/PricingPage";
import RegisterPremiumPage from "./pages/RegisterPremiumPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentCancelPage from "./pages/PaymentCancelPage";
import AppointmentsPage from "./pages/AppointmentsPage";
import AboutPage from "./pages/AboutPage";
import ServicesPage from "./pages/ServicesPage";
import BookingPage from "./pages/BookingPage";
import TherapistsPage from "./pages/TherapistsPage";
import OrientamentoPage from "./pages/OrientamentoPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import CookiePolicyPage from "./pages/CookiePolicyPage";
import TermsConditionsPage from "./pages/TermsConditionsPage";
import ScreeningPage from "./pages/ScreeningPage";
import ChatSpecialistaPage from "./pages/ChatSpecialistaPage";
import OnlyForYouPage from "./pages/OnlyForYouPage";
import CookieBanner from "./components/CookieBanner";
import Footer from "./components/Footer";

// IMT App Pages
import AppLayout from "./pages/app/AppLayout";
import AppDashboard from "./pages/app/AppDashboard";
import AICompanion from "./pages/app/AICompanion";
import MoodTracker from "./pages/app/MoodTracker";
import AssessmentCenter from "./pages/app/AssessmentCenter";
import AppProfile from "./pages/app/AppProfile";
import TestCompletatoPage from "./pages/app/TestCompletatoPage";
import SpecialistDashboard from "./pages/app/SpecialistDashboard";
import SpecialistMessages from "./pages/app/SpecialistMessages";
import SpecialistProgress from "./pages/app/SpecialistProgress";
import SpecialistReports from "./pages/app/SpecialistReports";
import NotificationSettings from "./pages/app/NotificationSettings";

// Navbar wrapper that hides on /app routes
const ConditionalNavbar = () => {
  const location = useLocation();
  if (location.pathname.startsWith('/app')) {
    return null;
  }
  return <Navbar />;
};

// Footer wrapper that hides on /app routes
const ConditionalFooter = () => {
  const location = useLocation();
  if (location.pathname.startsWith('/app')) {
    return null;
  }
  return <Footer />;
};

function AppContent() {
  return (
    <>
      <ScrollToTop />
      <ConditionalNavbar />
      <Routes>
        {/* IMT App Routes */}
        <Route path="/app" element={<AppLayout />}>
          <Route index element={<AppDashboard />} />
          <Route path="companion" element={<AICompanion />} />
          <Route path="mood" element={<MoodTracker />} />
          <Route path="assessment" element={<AssessmentCenter />} />
          <Route path="profile" element={<AppProfile />} />
          <Route path="test-completato" element={<TestCompletatoPage />} />
          <Route path="specialist" element={<SpecialistDashboard />} />
          <Route path="specialist/messages" element={<SpecialistMessages />} />
          <Route path="specialist/progress" element={<SpecialistProgress />} />
          <Route path="specialist/reports" element={<SpecialistReports />} />
          <Route path="settings/notifications" element={<NotificationSettings />} />
        </Route>

        {/* Main Website Routes */}
        <Route path="/" element={<main><HomePage /></main>} />
        <Route path="/about" element={<main><AboutPage /></main>} />
        <Route path="/services" element={<main><ServicesPage /></main>} />
        <Route path="/booking" element={<main><BookingPage /></main>} />
        <Route path="/therapists" element={<main><TherapistsPage /></main>} />
        <Route path="/orientamento" element={<main><OrientamentoPage /></main>} />
        <Route path="/tests" element={<main><TestsPage /></main>} />
        <Route path="/test/:testId" element={<main><TestPage /></main>} />
        <Route path="/results/:resultId" element={<main><ResultsPage /></main>} />
        <Route path="/auth" element={<main><AuthPage /></main>} />
        <Route path="/login" element={<main><AuthPage /></main>} />
        <Route path="/register" element={<main><AuthPage /></main>} />
        <Route path="/dashboard" element={<main><DashboardPage /></main>} />
        <Route path="/pricing" element={<main><PricingPage /></main>} />
        <Route path="/register-premium" element={<main><RegisterPremiumPage /></main>} />
        <Route path="/payment/success" element={<main><PaymentSuccessPage /></main>} />
        <Route path="/payment/cancel" element={<main><PaymentCancelPage /></main>} />
        <Route path="/appointments" element={<main><AppointmentsPage /></main>} />
        <Route path="/privacy-policy" element={<main><PrivacyPolicyPage /></main>} />
        <Route path="/cookie-policy" element={<main><CookiePolicyPage /></main>} />
        <Route path="/termini-condizioni" element={<main><TermsConditionsPage /></main>} />
        <Route path="/screening" element={<main><ScreeningPage /></main>} />
        <Route path="/chat-specialista" element={<main><ChatSpecialistaPage /></main>} />
        <Route path="/only-for-you" element={<main><OnlyForYouPage /></main>} />
      </Routes>
      <ConditionalFooter />
      <CookieBanner />
      <Toaster position="top-center" richColors />
    </>
  );
}

function App() {
  // Show maintenance page if enabled
  if (MAINTENANCE_MODE) {
    return <MaintenancePage />;
  }

  return (
    <LanguageProvider>
      <AuthProvider>
        <div className="App min-h-screen bg-background">
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </div>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
